import moment from 'moment';
import {isDate, isNumber, isString, reduce, filter} from 'lodash';
import {isEmptyVal} from "@/utils/index";
import { BILLING_CYCLE, NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP } from '@/common/constants';
import { locale } from '@/plugins/i18n';

export const dateTime = 'MMM D, YYYY';
export const dateTimeStr = 'MMM Do YYYY, h:mm:ss a';
export const dateFormatStr = 'YYYY-MM-DD';
export const dateTimeFormatStr = 'YYYY-MM-DD HH:mm:ss';

export const dateTimeFormat = (value, formatDateTime = dateTime) => {
  if (isEmptyVal(value)) {
    return '';
  } else if ((isDate(value) || isNumber(value) || isString(value)) && moment(value).isValid()) {
    // This method directly parses the provided date string into UTC time and creates a corresponding Moment object.
    // This means that the Moment object will always maintain the UTC time zone in subsequent operations,
    // no matter what operations are performed afterwards.
    return moment.utc(value).format(formatDateTime);
  } else if (moment.isMoment(value)) {
    return value.utc().format(formatDateTime);
  }

  return value;
};

export const dateTimeFormatWithClientZone = (value, formatDateTime = dateTime) => {
  if (isEmptyVal(value)) {
    return '';
  } else if ((isDate(value) || isNumber(value) || isString(value)) && moment(value).isValid()) {
    return moment(value).format(formatDateTime);
  } else if (moment.isMoment(value)) {
    return value.format(formatDateTime);
  }

  return value;
}

/**
 * value
 * @param value
 * @param options default utc zone
 * @return {string|*}
 */
export const dateTimeFormatWithUtcOffset = (value, options) => {
  const {formatDateTime = dateTime, offset = 0} = options || {};

  if (isEmptyVal(value)) {
    return '';
  } else if ((isDate(value) || isNumber(value) || isString(value)) && moment(value).isValid()) {
    return moment(value).utcOffset(offset).format(formatDateTime);
  } else if (moment.isMoment(value)) {
    return value.utcOffset(offset).format(formatDateTime);
  }

  return value;
};

export const addDaysWithFormatDate = (value, options) => {
  // offset default -8, means to convert to PST zone
  const {period = 1, unit = "day", offset = -8, formatDateTime = dateTime} = options;

  if (isEmptyVal(value)) {
    return '';
  } else if ((isDate(value) || isNumber(value) || isString(value)) && moment(value).isValid()) {
    return moment(value).utcOffset(offset).add(period, unit).format(formatDateTime);
  } else if (moment.isMoment(value)) {
    return value.utcOffset(offset).add(period, unit).format(formatDateTime);
  }

  return value;
}

export const currentPstDate = dateTimeFormatWithUtcOffset(Date.now(), {offset: -8});

// next annual/monthly pst date
export const getNextPstCycleDate = (options = {}) => {
  const {offset = -8, formatDateTime = dateFormatStr} = options;

  return {
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.ANNUAL]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'year',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.MONTHLY]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'month',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.QUARTERLY]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'quarter',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.TWO_YEARS]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 2,
      unit: 'year',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.THREE_YEARS]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 3,
      unit: 'year',
      formatDateTime,
    }),
  };
};


/**
 *
 * @param source
 * @param target
 * @return {boolean}
 */
export const compareDate = (source, target) => {
  if (moment(source).isValid() && moment(target).isValid()) {
    return moment(source) > moment(target);
  }

  return false;
}

/**
 *
 * @param discountPlans {expireDate: string} eg: {expireDate: '2022-11-20'}
 * @return {string}
 */
export const getEarliestTime = (discountPlans = []) => {
  const filterDiscountPlans = filter(discountPlans, (item) => !!item?.expireDate);

  return reduce(filterDiscountPlans, (pre, next) => {
    const { expireDate } = next || {};

    if (isEmptyVal(pre) || compareDate(pre, expireDate)) {
      pre = expireDate;
    }

    return pre;
  }, '')
}

const AllRegionDateFormat = {
  'en-US': 'MMMM D[,] YYYY',
  'de-DE': 'Do MMMM YYYY',
  'es-ES': 'D [de] MMMM [de] YYYY',
  'fr-FR': 'D MMMM YYYY',
  'id-ID': 'D MMMM YYYY',
  'it-IT': 'MMMM [in data] D[,] YYYY',
  'jp-JP': 'YYYY [年] M [月] D [日]',
  'ko-KO': 'YYYY[년] MMMM Do',
  'nl-NL': 'D MMMM YYYY',
  'pl-PL': 'D MMMM YYYY [r.]',
  'pt-PT': 'D [de] MMMM [de] YYYY',
  'ru-RU': 'D MMMM YYYY [г.]',
  'tr-TR': 'D MMMM YYYY',
  'vi-VN': 'D MMMM [năm] YYYY',
  'zh-CN': 'YYYY [年] M [月] D [日]',
  'zh-TW': 'YYYY [年] M [月] D [日]',
  'sv-SE': 'D MMMM YYYY',
  'gov-EN': 'MMMM D[,] YYYY',
  'en-GB': 'MMMM D[,] YYYY'
}
/**
 * get local data format string
 * @param {string} l local
 * @returns
 */
export const getLocalDateFormat = (l = locale) => {
  return AllRegionDateFormat[l]
}